import { useCallback, useMemo, useRef, useState } from "react";
import {
	Box,
	Button,
	Stack,
	Typography,
	Paper,
	Alert,
	Tooltip,
	Chip,
	CircularProgress,
	MenuItem,
	Select as MuiSelect,
	OutlinedInput,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import DownloadRounded from "@mui/icons-material/DownloadRounded";
import CloudUploadRounded from "@mui/icons-material/CloudUploadRounded";
import SendRounded from "@mui/icons-material/SendRounded";
import UploadRounded from "@mui/icons-material/UploadRounded";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { DataGrid, useGridApiContext } from "@mui/x-data-grid";
import axios from "axios";

import { PimDashboardState } from "context/PimDashboardContext";
import { GlobalState } from "context/GlobalContext";
import { inputTypes } from "data/globalData";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import {
	getBulkUploadRowStatusMessage,
	normalizeFlatRowStrings,
	validateAttributeValuesAgainstMaster,
} from "./bulkUploadStylesRules.js";

const excelColLetter = (index1Based) => {
	let n = index1Based;
	let s = "";
	while (n > 0) {
		const m = (n - 1) % 26;
		s = String.fromCharCode(65 + m) + s;
		n = Math.floor((n - 1) / 26);
	}
	return s;
};

/** Grid row validity (preview + aligns with POST rules) */
const getGridRowStatus = (gridRow, allAttrs, parsedRows) => {
	const flat = normalizeFlatRowStrings({ ...gridRow });
	delete flat.id;
	delete flat._rowStatus;
	delete flat._rowError;

	const typeErr = validateAttributeValuesAgainstMaster(flat, allAttrs);
	if (typeErr) return { ok: false, message: typeErr };

	if (flat.style_id && String(flat.style_id).trim()) return { ok: true, message: "" };

	const cre = getBulkUploadRowStatusMessage(flat, allAttrs, parsedRows);
	return cre ? { ok: false, message: cre } : { ok: true, message: "" };
};

const parseCsvText = (text) => {
	const lines = text.split(/\r?\n/).filter((l) => l.trim().length);
	if (!lines.length) return [];
	const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
	return lines.slice(1).map((line) => {
		const cells = line.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
		const row = {};
		headers.forEach((h, i) => {
			row[h] = cells[i] ?? "";
		});
		return row;
	});
};

const acceptedUploadExtensions = [".csv", ".xlsx", ".xlsm"];
const isAcceptedUploadFile = (file) => {
	const name = file?.name?.toLowerCase?.() ?? "";
	const ext = name.includes(".") ? name.slice(name.lastIndexOf(".")) : "";
	return acceptedUploadExtensions.includes(ext);
};

/** Custom edit cell for MULTIPLE_SELECT attributes */
const MultiSelectEditCell = ({ id, field, value, options }) => {
	const apiRef = useGridApiContext();
	const current = useMemo(() => {
		if (!value) return [];
		if (Array.isArray(value)) return value.map(String);
		const s = String(value).trim();
		if (!s) return [];
		if (s.startsWith("[")) {
			try { return JSON.parse(s).map(String); } catch { return []; }
		}
		return s.split(",").map((x) => x.trim()).filter(Boolean);
	}, [value]);

	const handleChange = (e) => {
		const selected = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
		apiRef.current.setEditCellValue({ id, field, value: selected.join(",") });
	};

	return (
		<MuiSelect
			multiple
			fullWidth
			size="small"
			value={current}
			onChange={handleChange}
			input={<OutlinedInput size="small" />}
			renderValue={(sel) => (
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
					{sel.map((v) => <Chip key={v} label={v} size="small" />)}
				</Box>
			)}
			sx={{ fontSize: 12 }}>
			{options.map((opt) => (
				<MenuItem key={opt} value={opt} dense>
					{opt}
				</MenuItem>
			))}
		</MuiSelect>
	);
};

const BULK_EXCLUDED_GROUP_IDS = ["fabric_details"];

const BulkUploadStyles = () => {
	const { allAttributes } = PimDashboardState();
	const { snackBar, setLoadingOpen, setSnackBarOpen, setSnackBar } = GlobalState();
	const fileInputRef = useRef(null);

	const [parsedRows, setParsedRows] = useState([]);
	const [submitResult, setSubmitResult] = useState(null);
	const [uploadFileName, setUploadFileName] = useState("");
	const [isDragging, setIsDragging] = useState(false);
	const [readingFile, setReadingFile] = useState(false);

	const bulkAttrs = useMemo(
		() => allAttributes.filter((a) => !BULK_EXCLUDED_GROUP_IDS.includes(a.group_id)),
		[allAttributes],
	);

	const templateAttrs = useMemo(
		() =>
			bulkAttrs.filter(
				(a) => a.input_type !== inputTypes.AUTO_GENERATE.key || a.key === "style_id",
			),
		[bulkAttrs],
	);

	const downloadTemplate = useCallback(async () => {
		const wb = new ExcelJS.Workbook();
		const dataSheet = wb.addWorksheet("Styles", { views: [{ state: "frozen", ySplit: 1 }] });
		const optionsSheet = wb.addWorksheet("Options");

		templateAttrs.forEach((attr, i) => {
			const col = i + 1;
			const c = dataSheet.getCell(1, col);
			c.value = attr.key;
			c.note = attr.name || attr.key;
		});

		templateAttrs.forEach((attr, i) => {
			const col = i + 1;
			optionsSheet.getCell(1, col).value = attr.key;
			const vals = attr.attributeValues || [];
			vals.forEach((v, ri) => {
				optionsSheet.getCell(ri + 2, col).value = v.value;
			});
		});

		const maxDataRow = 2000;
		templateAttrs.forEach((attr, i) => {
			if (attr.input_type !== inputTypes.SELECT.key && attr.input_type !== inputTypes.MULTIPLE_SELECT.key) return;
			const vals = attr.attributeValues || [];
			if (!vals.length) return;
			const colLetter = excelColLetter(i + 1);
			const lastOptRow = vals.length + 1;
			const formula = `=Options!$${colLetter}$2:$${colLetter}$${lastOptRow}`;
			dataSheet.dataValidations.add(`${colLetter}2:${colLetter}${maxDataRow}`, {
				type: "list",
				allowBlank: true,
				formulae: [formula],
			});
		});

		const buf = await wb.xlsx.writeBuffer();
		saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "bulk_styles_template.xlsx");
	}, [templateAttrs]);

	const parseWorkbook = async (file) => {
		const name = file.name?.toLowerCase() || "";
		if (name.endsWith(".csv")) {
			const text = await file.text();
			return parseCsvText(text);
		}
		const wb = new ExcelJS.Workbook();
		const ab = await file.arrayBuffer();
		await wb.xlsx.load(ab);
		const ws = wb.getWorksheet("Styles") || wb.worksheets[0];
		if (!ws) return [];
		const headerRow = ws.getRow(1);
		const headers = [];
		headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
			headers[colNumber - 1] = cell.value != null ? String(cell.value).trim() : "";
		});
		const maxCol = headers.length;
		const rows = [];
		ws.eachRow((row, rowNumber) => {
			if (rowNumber === 1) return;
			const obj = {};
			let empty = true;
			for (let c = 1; c <= maxCol; c++) {
				const key = headers[c - 1];
				if (!key) continue;
				const cell = row.getCell(c);
				let v = cell.value;
				if (v && typeof v === "object" && "text" in v) v = v.text;
				if (v && typeof v === "object" && "result" in v) v = v.result;
				if (v !== null && v !== undefined && String(v).trim() !== "") empty = false;
				obj[key] = v === null || v === undefined ? "" : v;
			}
			if (!empty) rows.push(obj);
		});
		return rows;
	};

	const processSelectedFile = async (file) => {
		if (!file) return;
		if (!isAcceptedUploadFile(file)) {
			setSnackBarOpen(true);
			setSnackBar((prev) => ({
				...prev,
				message: "Please upload a .xlsx, .xlsm, or .csv file",
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			}));
			return;
		}
		setUploadFileName(file.name);
		setSubmitResult(null);
		setReadingFile(true);
		setLoadingOpen(true);
		try {
			const rows = await parseWorkbook(file);
			setParsedRows(rows);
			setSnackBarOpen(true);
			setSnackBar((prev) => ({
				...prev,
				message: `Loaded ${rows.length} row(s)`,
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			}));
		} catch (err) {
			setSnackBarOpen(true);
			setSnackBar((prev) => ({ ...prev, message: parseErrorMessage(err), severity: "error" }));
		} finally {
			setReadingFile(false);
			setLoadingOpen(false);
		}
	};

	const onFileInputChange = async (e) => {
		const file = e.target.files?.[0];
		e.target.value = "";
		await processSelectedFile(file);
	};

	const openFilePicker = () => fileInputRef.current?.click();

	const onDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(true);
	};

	const onDragLeave = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	};

	const onDrop = async (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
		const file = e.dataTransfer.files?.[0];
		await processSelectedFile(file);
	};

	const gridColumns = useMemo(() => {
		const attrMap = Object.fromEntries(bulkAttrs.map((a) => [a.key, a]));
		const keys = templateAttrs.map((a) => a.key);
		const cols = keys.map((k) => {
			const attr = attrMap[k];
			const isAutoGen = attr?.input_type === inputTypes.AUTO_GENERATE.key;
			const isSelect = attr?.input_type === inputTypes.SELECT.key;
			const isMultiSelect = attr?.input_type === inputTypes.MULTIPLE_SELECT.key;
			const options = (attr?.attributeValues || []).map((v) => v.value);

			const base = {
				field: k,
				headerName: k,
				minWidth: 120,
				flex: 1,
				editable: !isAutoGen,
				renderCell: (params) => {
				const st = getGridRowStatus(params.row, bulkAttrs, parsedRows);
				const invalid = !st.ok;
					return (
						<Tooltip title={invalid ? st.message : ""}>
							<Box
								sx={{
									width: "100%",
									height: "100%",
									display: "flex",
									alignItems: "center",
									px: 0.5,
									backgroundColor: invalid ? "error.light" : "transparent",
								}}>
								<Typography variant="body2" noWrap>
									{params.value != null ? String(params.value) : ""}
								</Typography>
							</Box>
						</Tooltip>
					);
				},
			};

			if (isSelect && options.length) {
				return {
					...base,
					type: "singleSelect",
					valueOptions: ["", ...options],
				};
			}

			if (isMultiSelect && options.length) {
				return {
					...base,
					renderEditCell: (params) => (
						<MultiSelectEditCell
							id={params.id}
							field={params.field}
							value={params.value}
							options={options}
						/>
					),
				};
			}

			return base;
		});
		cols.unshift({
			field: "_rowStatus",
			headerName: "Status",
			width: 160,
			sortable: false,
			editable: false,
			valueGetter: (_value, row) => {
				const st = getGridRowStatus(row, bulkAttrs, parsedRows);
				return st.ok ? "Will be submitted" : "Will be rejected";
			},
			renderCell: (params) => {
				const st = getGridRowStatus(params.row, bulkAttrs, parsedRows);
				return st.ok ? (
					<Chip size="small" label="Will be submitted" color="success" variant="outlined" />
				) : (
					<Tooltip title={st.message}>
						<Chip size="small" label="Will be rejected" color="error" variant="outlined" />
					</Tooltip>
				);
			},
		});
		return cols;
	}, [templateAttrs, bulkAttrs, parsedRows]);

	const rowsWithIds = useMemo(
		() => parsedRows.map((r, i) => ({ id: i, ...r, _rowStatus: "", _rowError: "" })),
		[parsedRows],
	);

	const handleSubmit = async () => {
		if (!parsedRows.length) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: "No rows to submit", severity: "warning" });
			return;
		}
		setLoadingOpen(true);
		setSubmitResult(null);
		try {
			const res = await axios.post("/api/designer/bulk_upload_styles", { rows: parsedRows });
			setSubmitResult(res.data);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `Done: ${res.data.success?.length || 0} succeeded, ${res.data.failed?.length || 0} failed`,
				severity: "success",
			});
		} catch (err) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(err), severity: "error" });
		} finally {
			setLoadingOpen(false);
		}
	};

	return (
		<Box width="100%" maxWidth="100%" overflow="hidden" sx={{ p: { xs: 1.5, sm: 2 } }}>
			<Box width="100%" display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} pt={1} pb={2}>
				<Box>
					<Typography variant="h5">Bulk upload styles</Typography>
					<Typography variant="body2" color="text.secondary">
						Create or replace styles from Excel or CSV (same flow as other PIM uploads).
					</Typography>
				</Box>
			</Box>

			<Paper
				elevation={0}
				sx={{
					border: `1px solid ${grey[200]}`,
					borderRadius: 3,
					p: 2,
					mb: 2,
					background:
						"linear-gradient(180deg, rgba(245,245,245,0.55) 0%, rgba(255,255,255,1) 30%, rgba(255,255,255,1) 100%)",
					boxSizing: "border-box",
				}}>
				<Alert severity="info" sx={{ mb: 2, backgroundColor: grey[200], color: grey[900], "& .MuiAlert-icon": { color: grey[700] }, "& .MuiAlert-message": { color: grey[900] } }}>
					Download the template for attribute keys, an <strong>Options</strong> sheet, and dropdowns on select fields (one row = one style).
					<strong> New style:</strong> only <em>mandatory</em> Style and Color fields must be filled; Colour / Palette / Theme / Style ref may vary per row — all other Color fields must match the first new row.
					<strong> Update:</strong> <strong>style_id</strong> required; only include columns you wish to overwrite.
				</Alert>

				<Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
					<Box
						role="button"
						tabIndex={0}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								openFilePicker();
							}
						}}
						onClick={openFilePicker}
						onDragOver={onDragOver}
						onDragLeave={onDragLeave}
						onDrop={onDrop}
						sx={{
							flex: 1,
							minHeight: 200,
							border: isDragging ? `2px dashed ${grey[900]}` : `1px dashed ${grey[300]}`,
							borderRadius: 3,
							backgroundColor: isDragging ? grey[100] : grey[50],
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							cursor: "pointer",
							transition: "border-color 0.15s ease, background-color 0.15s ease",
							px: 2,
							py: 3,
						}}>
						<Stack alignItems="center" spacing={1.5} textAlign="center">
							{readingFile ? (
								<CircularProgress size={40} />
							) : (
								<CloudUploadRounded sx={{ fontSize: 48, color: grey[600] }} />
							)}
							<Typography variant="subtitle1" fontWeight={600}>
								{readingFile ? "Reading file…" : "Drop file here or click to upload"}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Excel (.xlsx, .xlsm) or CSV — first sheet or tab named <strong>Styles</strong>
							</Typography>
							<Button
								variant="outlined"
								size="small"
								startIcon={<UploadRounded />}
								onClick={(e) => {
									e.stopPropagation();
									openFilePicker();
								}}
								disabled={readingFile}>
								Choose file
							</Button>
							<input
								ref={fileInputRef}
								type="file"
								hidden
								accept=".xlsx,.xlsm,.csv"
								onChange={onFileInputChange}
							/>
						</Stack>
					</Box>

					<Stack
						spacing={1.5}
						sx={{
							width: { xs: "100%", md: 280 },
							flexShrink: 0,
						}}>
						<Button
							fullWidth
							variant="outlined"
							startIcon={<DownloadRounded />}
							onClick={downloadTemplate}>
							Download template
						</Button>
					<Button
						fullWidth
						variant="contained"
						startIcon={<SendRounded />}
						onClick={handleSubmit}
						disabled={!parsedRows.length || readingFile}
						sx={{
							backgroundColor: grey[900],
							"&:hover": { backgroundColor: grey[800] },
							"&.Mui-disabled": { backgroundColor: grey[300], color: grey[500] },
						}}>
						Submit For Changes
					</Button>
						{uploadFileName && (
							<Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
								{uploadFileName}
								{parsedRows.length > 0 ? ` — ${parsedRows.length} row(s) read` : ""}
							</Typography>
						)}
					</Stack>
				</Stack>
			</Paper>

			{parsedRows.length > 0 && (
				<Paper
					elevation={0}
					sx={{
						height: 480,
						width: "100%",
						mb: 2,
						border: `1px solid ${grey[200]}`,
						borderRadius: 3,
						overflow: "hidden",
						background: "#fff",
					}}>
					<DataGrid
					rows={rowsWithIds}
					columns={gridColumns}
					disableRowSelectionOnClick
					density="compact"
					processRowUpdate={(updatedRow) => {
						const { id: rowId, _rowStatus: _s, _rowError: _e, ...fields } = updatedRow;
						setParsedRows((prev) => {
							const next = [...prev];
							next[rowId] = { ...next[rowId], ...fields };
							return next;
						});
						return updatedRow;
					}}
					onProcessRowUpdateError={() => {}}
				/>
				</Paper>
			)}

			{submitResult && (
				<Stack spacing={1}>
					<Alert severity="info">
						Succeeded: {submitResult.success?.length || 0} &mdash; Failed: {submitResult.failed?.length || 0}
					</Alert>
					{submitResult.failed?.length > 0 && (
						<Paper
							elevation={0}
							sx={{ border: `1px solid ${grey[200]}`, borderRadius: 2, p: 1.5, maxHeight: 200, overflow: "auto" }}>
							<Typography variant="subtitle2" gutterBottom>
								Failed rows
							</Typography>
							{submitResult.failed.map((f) => (
								<Typography key={f.rowIndex} variant="caption" display="block">
									Row {f.rowIndex + 1} {f.style_id ? `(style_id: ${f.style_id})` : ""}: {f.reason}
								</Typography>
							))}
						</Paper>
					)}
					{submitResult.success?.length > 0 && (
						<Paper
							elevation={0}
							sx={{ border: `1px solid ${grey[200]}`, borderRadius: 2, p: 1.5, maxHeight: 160, overflow: "auto" }}>
							<Typography variant="subtitle2" gutterBottom>
								Succeeded
							</Typography>
							{submitResult.success.map((s) => (
								<Typography key={s.rowIndex} variant="caption" display="block">
									Row {s.rowIndex + 1}: {s.style_id} ({s.action})
								</Typography>
							))}
						</Paper>
					)}
				</Stack>
			)}
		</Box>
	);
};

export default BulkUploadStyles;
